import { useMemo, useState } from "react";
import { C } from "../../shared/theme";
import { pageColumn } from "../../shared/layout";
import { BottomNav } from "../../shared/BottomNav";
import { TopBar } from "../../shared/TopBar";
import { HELP } from "../../shared/helpContent";
import { intro } from "../../shared/textStyles";
import { GroupHeader } from "../../shared/GroupHeader";
import { AddButton } from "../../shared/AddButton";
import { useViewState } from "../../shared/useViewState";
import { useAddRequest } from "../../shared/useAddRequest";
import { categoryGroups, moveInCategory } from "./compassLists";
import { COMPASS_KINDS, compassCount } from "./compassKinds";
import { CategoryItemEditor } from "./CategoryItemEditor";
import { CompassRow, SortableRows } from "./CompassParts";
import { listCard } from "../../shared/ListRow";

// Habits and Temptations: grouped by the user's categories like Fears (Craig, 24 Oct 2026: "I'd like Habits to be
// linked to the user's Categories"; Temptations the same, 24 Oct 2026). Each category keeps its own list on the
// compass; Today's Reminders chooses from all of them. Which categories are folded resets each session.
// `kind` is "habits" or "temptations"; `goals` are offered in a habit's form.
export function CompassGroupsPage({ kind, uid, compass, setCompass, goals = [], onNav, addRequested, onAddHandled }) {
  const k = COMPASS_KINDS[kind];
  const categories = useMemo(() => compass?.categories || [], [compass]);
  const groups = useMemo(() => categoryGroups(categories, k.field), [categories, k.field]);
  const [editing, setEditing] = useState(null); // null, "new", or { categoryId, index }
  const [view, setView] = useViewState(`nudge-${kind}-ui-${uid}`);
  // With no categories nothing can be saved: the page's sentence says where to add one instead.
  useAddRequest(addRequested, onAddHandled, () => { if (categories.length) setEditing("new"); });
  const isOpen = (id) => view.openGroups?.[id] ?? true;
  const onMove = (categoryId, items, from, to) =>
    setCompass(p => ({ ...p, categories: moveInCategory(p.categories || [], k.field, categoryId, items[from].index, items[to].index) }));
  const isNew = editing === "new";
  const from = isNew ? null : editing;

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: C.bg, color: C.text, position: "relative" }}>
      {editing !== null && (
        <CategoryItemEditor kind={k} categories={categories} goals={goals} from={from}
          initialCategoryId={categories.length === 1 ? categories[0].id : ""} setCompass={setCompass}
          onClose={() => setEditing(null)} />
      )}
      <TopBar title={k.title} help={HELP[kind]} />
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ ...pageColumn, padding: "4px 16px 24px" }}>
          {groups.length === 0 ? (
            <div style={{ padding: "12px 4px 0", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 16 }}>
              <p style={intro()}>
                {categories.length === 0
                  ? `Each ${k.one} belongs to one of your categories, and you don't have any yet. You can add one in Settings, under Manage Categories.`
                  : k.empty}
              </p>
              {categories.length > 0
                ? <AddButton onClick={() => setEditing("new")}>{`Add a ${k.one}`}</AddButton>
                : <AddButton onClick={() => onNav("settings")}>Open Settings</AddButton>}
            </div>
          ) : (
            <>
              <p style={{ ...intro(), margin: "8px 4px 16px" }}>
                {k.intro} Each day you can choose up to three to show in Today's Reminders, on the Today page.
              </p>
              {groups.map(({ category, items }) => (
                <section key={category.id} aria-label={category.label} style={{ marginBottom: 16 }}>
                  <GroupHeader label={category.label} icon={category.icon} countText={compassCount(kind, items.length)}
                    open={isOpen(category.id)} onToggle={() => setView({ openGroups: { ...view.openGroups, [category.id]: !isOpen(category.id) } })} />
                  {isOpen(category.id) && (
                    <div style={{ ...listCard(), marginTop: 4 }}>
                      {/* One drag list per category, so an item moves only within its own (Craig, 26 Oct 2026). */}
                      <SortableRows texts={items.map(it => it.text)} onMove={(from, to) => onMove(category.id, items, from, to)}>
                        {(i, handle) => {
                          const { item, text, index } = items[i];
                          return <CompassRow habit={k.arrows ? item : null} text={text} first={index === 0} editLabel={`Edit ${k.one}`}
                            onEdit={() => setEditing({ categoryId: category.id, index })} handle={handle} />;
                        }}
                      </SortableRows>
                    </div>
                  )}
                </section>
              ))}
            </>
          )}
        </div>
      </div>
      <BottomNav active={null} onNav={onNav} />
    </div>
  );
}
